import type { AuthUser, Role } from '../types'

const STORAGE_KEY = 'mmc_auth'

export interface StoredAuth {
  token: string
  role: Role
  user: AuthUser
}

export function getStoredAuth(): StoredAuth | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredAuth
  } catch {
    return null
  }
}

export function setStoredAuth(auth: StoredAuth | null): void {
  if (auth) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(auth))
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export class ApiError extends Error {
  status: number

  constructor(status: number, body: unknown) {
    const message =
      body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `Error inesperado (${status})`
    super(message)
    this.status = status
  }
}

interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const auth = getStoredAuth()
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  let body: BodyInit | undefined
  if (options.body instanceof FormData) {
    // No seteamos Content-Type: el navegador arma el boundary multipart solo.
    body = options.body
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.body)
  }

  if (auth) headers.set('Authorization', `Bearer ${auth.token}`)

  const response = await fetch(path, { ...options, headers, body })

  // Una sesion expirada no debe interrumpir un intento de login fallido:
  // /auth/login tambien responde 401 con credenciales invalidas.
  if (response.status === 401 && path !== '/auth/login') {
    setStoredAuth(null)
    window.location.assign('/login')
    throw new ApiError(401, null)
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null)
    throw new ApiError(response.status, errorBody)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}
